import React, { useState, useRef, useCallback } from "react";
import {
  Dialog,
  Button,
  IconButton,
  LinearProgress,
  Typography,
  Box,
  useTheme,
  Grow,
  Fade,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadImagesToFirebase } from "../../Inventory/WriteFunction/uploadImagesToFirebase";

const MultipleImageUploader = ({ onUploadComplete, basePath = "images" }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);
  const theme = useTheme();

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => {
    if (!isUploading) setIsModalOpen(false);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    addNewImages(files);
  };

  const addNewImages = (files) => {
    let baseTimestamp = new Date().getTime();

    const newImageObjects = files.map((file, index) => {
      const uniqueTimestamp = baseTimestamp + index;

      return {
        id: `image_${uniqueTimestamp}_${Math.random()
          .toString(36)
          .substring(2, 9)}`,
        file: file,
        name: file.name,
        preview: URL.createObjectURL(file),
        date: new Date().toISOString(),
        timestamp: uniqueTimestamp,
        size: (file.size / 1024 / 1024).toFixed(2),
      };
    });

    setSelectedImages((prev) => [...prev, ...newImageObjects]);
  };

  const handleRemoveImage = (idToRemove) => {
    setSelectedImages((prev) => {
      const updatedImages = prev.filter((image) => image.id !== idToRemove);
      const imageToRemove = prev.find((img) => img.id === idToRemove);
      if (imageToRemove && imageToRemove.preview) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return updatedImages;
    });
  };

  const simulateProgress = () => {
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 10;
      if (progress > 95) {
        clearInterval(interval);
        progress = 95;
      }
      setUploadProgress(Math.min(progress, 95));
    }, 200);
    return interval;
  };

  const handleUploadImages = async () => {
    if (selectedImages.length === 0) return;

    setIsUploading(true);
    setUploadProgress(5); // Start with visual progress immediately

    try {
      // First process the small images to show quick progress
      const smallImages = selectedImages.filter(
        (img) => img.file.size < 1 * 1024 * 1024
      ); // Under 1MB
      const largeImages = selectedImages.filter(
        (img) => img.file.size >= 1 * 1024 * 1024
      );

      // Set upload progress based on the number of small images
      if (smallImages.length > 0) {
        setUploadProgress(
          10 + (smallImages.length / selectedImages.length) * 15
        );
      }

      // Upload all images in parallel with progress tracking
      const urls = await uploadImagesToFirebase(
        selectedImages,
        basePath,
        (progress) => {
          // Update progress state with real progress
          setUploadProgress(progress);
        }
      );

      // Prepare the uploaded images data structure
      const uploadedImages = {};

      selectedImages.forEach((image, index) => {
        if (index < urls.length) {
          const timestamp = image.timestamp.toString();
          uploadedImages[timestamp] = {
            url: urls[index],
            name: image.name,
            date: image.date,
          };
        }
      });

      // Complete the upload process
      setUploadProgress(100);

      // Small timeout for a smoother experience before dismissing the dialog
      setTimeout(() => {
        onUploadComplete(uploadedImages);
        setSelectedImages([]);
        handleCloseModal();
      }, 300); // Reduced from 500ms to 300ms for faster completion
    } catch (error) {
      console.error("Error in uploading images:", error);
    } finally {
      // Ensure we always reset the uploading state
      setTimeout(() => {
        setIsUploading(false);
      }, 300); // Reduced from 500ms to 300ms
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addNewImages(Array.from(e.dataTransfer.files));
      }
    },
    [addNewImages]
  );

  return (
    <div
      className="image-uploader-container"
      style={{
        position: "relative",
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Button
        variant="contained"
        sx={{
          backgroundColor: "#F0F7FF",
          color: "#1677FF",
          border: "1px dashed #91CAFF",
          borderRadius: "8px",
          padding: "8px 14px",
          textTransform: "none",
          alignItems: "center",
          display: "flex",
          fontSize: "14px",
          fontWeight: 500,
          transition: "transform 0.2s, box-shadow 0.2s",
          "&:hover": {
            transform: "scale(1.00)",
            boxShadow: "0 4px 8px rgba(25, 118, 210, 0.25)",
            color: "#1677FF",
            backgroundColor: "#E6F0FF",
          },
        }}
        onClick={handleOpenModal}
        startIcon={<CloudUploadIcon />}
        disableElevation
      >
        Upload Images
      </Button>

      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 16px 32px rgba(0, 0, 0, 0.15)",
            maxHeight: "85vh", // Constrains the dialog height
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header - Fixed */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2.5,
            borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
            background: "white",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Upload Images
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            disabled={isUploading}
            sx={{
              color: theme.palette.grey[600],
              transition: "transform 0.2s",
              "&:hover": {
                transform: "rotate(90deg)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Progress bar */}
        {isUploading && (
          <Fade in={isUploading} timeout={300}>
            <Box
              sx={{
                width: "100%",
                position: "sticky",
                top: "64px",
                zIndex: 9,
              }}
            >
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                sx={{
                  height: 4,
                  "& .MuiLinearProgress-bar": {
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  },
                }}
              />
            </Box>
          </Fade>
        )}

        {/* Content - Scrollable */}
        <Box
          sx={{
            p: 3,
            pt: isUploading ? 5 : 3,
            overflowY: "auto", // Makes this box scrollable
            flex: 1, // Takes up available space
          }}
        >
          {/* Upload status */}
          {isUploading && (
            <Fade in={isUploading} timeout={300}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 3,
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "rgba(25, 118, 210, 0.06)",
                }}
              >
                <CloudUploadIcon
                  sx={{ mr: 2, color: theme.palette.primary.main }}
                />
                <Typography
                  variant="body1"
                  sx={{ fontWeight: 500, color: theme.palette.primary.main }}
                >
                  Uploading {selectedImages.length}{" "}
                  {selectedImages.length === 1 ? "image" : "images"}
                  {" - "}
                  {Math.round(uploadProgress)}%
                </Typography>
              </Box>
            </Fade>
          )}

          {/* Drag and drop area */}
          <Box
            sx={{
              border: `2px dashed ${
                dragging ? theme.palette.primary.main : theme.palette.grey[300]
              }`,
              borderRadius: "16px",
              p: 3,
              mb: 3,
              backgroundColor: dragging
                ? "rgba(25, 118, 210, 0.04)"
                : "transparent",
              transition: "all 0.2s ease",
              cursor: "pointer",
              textAlign: "center",
              transform: dragging ? "scale(1.02)" : "scale(1)",
            }}
            onClick={triggerFileInput}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <AddPhotoAlternateIcon
              sx={{
                fontSize: 54,
                color: dragging
                  ? theme.palette.primary.main
                  : theme.palette.grey[400],
                mb: 1.5,
                transition: "color 0.2s",
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
              Drop images here or click to browse
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Supported formats: JPG, PNG, GIF, WEBP
            </Typography>
          </Box>

          {/* Selected images grid */}
          {selectedImages.length > 0 && (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    marginRight: "8px",
                    fontSize: "14px",
                  }}
                >
                  {selectedImages.length}
                </span>
                {selectedImages.length === 1 ? "image" : "images"} selected
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)", // 6 images per row
                  gap: 1.5,
                }}
              >
                {selectedImages.map((image) => (
                  <Fade key={image.id} in={true} timeout={300}>
                    <Box
                      sx={{
                        borderRadius: "12px",
                        overflow: "hidden",
                        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.06)",
                        backgroundColor: "white",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        position: "relative",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 16px rgba(0, 0, 0, 0.12)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          paddingTop: "75%", // 4:3 aspect ratio
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            backgroundImage: `url(${image.preview})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            transition: "transform 0.3s ease",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ p: 1.2 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontSize: "0.8rem",
                          }}
                        >
                          {image.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{ fontSize: "0.7rem" }}
                        >
                          {image.size} MB
                        </Typography>
                      </Box>

                      <IconButton
                        size="small"
                        disabled={isUploading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage(image.id);
                        }}
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          color: "white",
                          padding: "3px",
                          width: "28px",
                          height: "28px",
                          transition: "transform 0.2s, background-color 0.2s",
                          "&:hover": {
                            backgroundColor: "rgba(255, 59, 48, 0.8)",
                            transform: "scale(1.1)",
                          },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: "16px" }} />
                      </IconButton>
                    </Box>
                  </Fade>
                ))}
              </Box>
            </Box>
          )}

          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            ref={fileInputRef}
            style={{ display: "none" }}
          />
        </Box>

        {/* Actions - Fixed at bottom */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            p: 2.5,
            borderTop: "1px solid rgba(0, 0, 0, 0.06)",
            backgroundColor: "white",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleCloseModal}
            disabled={isUploading}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={selectedImages.length === 0 || isUploading}
            onClick={handleUploadImages}
            startIcon={<CloudUploadIcon />}
            sx={{
              borderRadius: "10px",
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
              textTransform: "none",
              fontWeight: 500,
              padding: "10px 24px",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "scale(1.02)",
                boxShadow: "0 6px 14px rgba(25, 118, 210, 0.4)",
              },
              "&:active": {
                transform: "scale(0.98)",
              },
              "&:disabled": {
                background: theme.palette.grey[300],
              },
            }}
          >
            {isUploading
              ? `Uploading (${Math.round(uploadProgress)}%)`
              : `Upload ${
                  selectedImages.length > 0 ? selectedImages.length : ""
                } ${
                  selectedImages.length === 1
                    ? "Image"
                    : selectedImages.length > 0
                    ? "Images"
                    : "All Images"
                }`}
          </Button>
        </Box>
      </Dialog>
    </div>
  );
};

export default MultipleImageUploader;
